import React, { useEffect, useState } from "react";
import {
  dateObjToDateStringDashedinverted,
  dateStringToDateObj,
  dateStringToDateObjNoTime,
  dateStringToDateStringReverse,
  deepCloneObject,
  IItinerary,
  useConfig,
} from "@thekilroygroup/shared-ui";
import { ReturnCalendar } from "./ReturnCalendar";
import { IPriceItinerary, IPriceDates } from "../interfaces.lowPrice";
import { useLazyFetchFlightsQuery } from "redux/services/flightSearch";
import {
  buildLowPriceData,
  createEmptyCheapestItineraries,
} from "../buildLowPriceData";
import { addDays, differenceInDays } from "date-fns";
import { IFlight, ISearchForm } from "redux/searchForm";
import { ISearchFormValidation } from "validation/validation.interfaces";
import { validateSearchForm } from "validation/validateSearch";
import { removeDublicatedEntries } from "../methods";
import useQueryParams, { IQueryParams } from "hooks/useQueryParams";
import useSetQueryParams from "hooks/useSetQueryParams";
import { IAppConfigObject } from "appConfig.interfaces";

interface ReturnCalendarWrapperProps {
  selectedItineraryId: string | null;
  selectedItineraryDates: {
    departureDate: string;
    returnDate: string;
  } | null;
  setSelectedItinerary: (itinerary: IItinerary) => void;
  showYouthStudentItineraries: boolean;
  setSelectedItineraryIsUpdated: (isUpdated: boolean) => void;
  setCustomErrorCodes: (codes: string[]) => void;
  navigateToSearchPage: () => void;
  flights: IFlight[];
}

export const ReturnCalendarWrapper: React.FC<ReturnCalendarWrapperProps> = ({
  selectedItineraryId,
  selectedItineraryDates,
  setSelectedItinerary,
  showYouthStudentItineraries,
  setSelectedItineraryIsUpdated,
  setCustomErrorCodes,
  navigateToSearchPage,
  flights,
}) => {
  const [cheapestCombinations, setCheapestCombinations] = useState<
    IPriceItinerary[] | []
  >([]);
  const [emptyCheapestCombinations, setEmptyCheapestCombinations] = useState<
    IPriceItinerary[]
  >([]);
  const [cheapestItineraries, setCheapestItineraries] = useState<{
    [key: string]: IItinerary;
  }>({});

  const params: IQueryParams = useQueryParams();
  useSetQueryParams();
  const { appConfig } = useConfig<IAppConfigObject>();

  const [trigger, result] = useLazyFetchFlightsQuery();
  const { isFetching, isError, data, originalArgs, error } = result;

  const departureDate = dateStringToDateObjNoTime(
    flights[0].departureDate,
  ) as Date;
  const returnDate = dateStringToDateObjNoTime(
    flights[1].departureDate,
  ) as Date;

  const [searchedDates, setSearchedDates] = useState<IPriceDates>({
    departureDate,
    returnDate,
  });
  const [searchQue, setSearchQue] = useState<IPriceDates[]>([]);

  const handleChangeDates = () => {
    const departureDate = dateStringToDateObjNoTime(
      flights[0].departureDate,
    ) as Date;
    const returnDate = dateStringToDateObjNoTime(
      flights[1].departureDate,
    ) as Date;

    setSearchedDates({ departureDate, returnDate });

    const initialEmptyItineraries = createEmptyCheapestItineraries(
      departureDate,
      returnDate,
      departureDate,
      returnDate,
    );

    setEmptyCheapestCombinations((prevState) => [
      ...prevState,
      ...initialEmptyItineraries,
    ]);

    setSearchQue((prevState) => [
      ...prevState,
      {
        departureDate: departureDate,
        returnDate: returnDate,
      },
    ]);
  };

  /**
   * @param departureDate yyyy-mm-dd
   * @param returnDate yyyy-mm-dd
   */
  const triggerRefetch = (
    departureDate: string | null = dateStringToDateStringReverse(
      flights[0].departureDate,
    ),
    returnDate: string | null = dateStringToDateStringReverse(
      flights[1].departureDate,
    ),
  ) => {
    let nextParams: ISearchForm = deepCloneObject(params.searchQuery);
    nextParams.flexibleSearch = 3;
    nextParams.flights[0].departureDate =
      dateStringToDateStringReverse(departureDate);
    nextParams.flights[1].departureDate =
      dateStringToDateStringReverse(returnDate);
    trigger({ searchForm: nextParams, config: appConfig });
  };

  const [firstFetch, setFirstFetch] = useState(true);

  useEffect(() => {
    if (!isFetching && firstFetch && isError) {
      const dateRelatedErrors = error.data.customErrors.filter(
        (e: { errorCode: string }) =>
          e.errorCode === "4002" || e.errorCode === "4003",
      );
      if (dateRelatedErrors) {
        setCustomErrorCodes(
          dateRelatedErrors.map((e: { errorCode: string }) => e.errorCode),
        );
        return;
      }
      navigateToSearchPage();
    } else if (!isError && data && !isFetching && searchedDates) {
      const { flights }: ISearchForm = originalArgs.searchForm;
      const { departureDate, returnDate } = searchedDates;
      const offsetDepartureDays = differenceInDays(
        dateStringToDateObj(flights[0].departureDate) as Date,
        departureDate,
      );
      const offsetReturnDays = differenceInDays(
        dateStringToDateObj(flights[1].departureDate) as Date,
        returnDate,
      );

      const priceData = buildLowPriceData(
        data.apiResponse.itineraries,
        departureDate,
        returnDate,
        offsetDepartureDays,
        offsetReturnDays,
      );

      setCheapestItineraries((prevState) => ({
        ...prevState,
        ...deepCloneObject(priceData.cheapestItineraries),
      }));

      const unique = removeDublicatedEntries(
        priceData.cheapestCombinations,
        cheapestCombinations,
      );

      const uniqueEmpty: IPriceItinerary[] = [];
      emptyCheapestCombinations.forEach((empty: IPriceItinerary) => {
        if (
          !unique.some(
            (uniqueItin: IPriceItinerary) =>
              uniqueItin.departureDate === empty.departureDate &&
              uniqueItin.returnDate === empty.returnDate,
          )
        ) {
          uniqueEmpty.push(empty);
        }
      });

      setCheapestCombinations(() => [...unique]);
      setEmptyCheapestCombinations(() => [...uniqueEmpty]);

      if (firstFetch) {
        setFirstFetch(false);
        const firstItinerary = unique.find(
          (itn) =>
            itn.departureDateDifference === 0 && itn.returnDateDifference === 0,
        );
        if (!firstItinerary) return;

        const id =
          firstItinerary.hasStudentOrYouth &&
          firstItinerary.studentOrYouthData.price <= firstItinerary.price
            ? firstItinerary.studentOrYouthData.id
            : firstItinerary.id;
        setSelectedItinerary(priceData.cheapestItineraries[id]);
      }
      setSelectedItineraryIsUpdated(true);
    }
  }, [isFetching]);

  useEffect(() => {
    if (!isFetching && searchQue.length) {
      triggerRefetch(
        dateObjToDateStringDashedinverted(searchQue[0].departureDate),
        dateObjToDateStringDashedinverted(searchQue[0].returnDate),
      );
      setSearchQue((prevState) => prevState.slice(1));
    }
  }, [isFetching, searchQue]);

  /**
   *
   * @param departureDate Date
   * @param returnDate Date
   */
  const loadMoreData = (departureDate: Date, returnDate: Date) => {
    const nextCheapestCombinations = createEmptyCheapestItineraries(
      departureDate,
      returnDate,
      searchedDates?.departureDate as Date,
      searchedDates?.returnDate as Date,
    );

    setEmptyCheapestCombinations((prevState) => [
      ...prevState,
      ...nextCheapestCombinations,
    ]);

    const firstAllowedDepartureDate = addDays(new Date(), 3);
    const isDepartureBeforeFirstAllowedDate =
      differenceInDays(departureDate, firstAllowedDepartureDate) < 0;

    const isDepartureBeforeReturn =
      differenceInDays(returnDate, departureDate) < 0;
    const diffReturn = differenceInDays(returnDate, departureDate);

    if (isDepartureBeforeFirstAllowedDate) {
      setSearchQue((prevState) => [
        ...prevState,
        {
          departureDate: firstAllowedDepartureDate,
          returnDate: returnDate,
        },
      ]);
    } else if (isDepartureBeforeReturn) {
      setSearchQue((prevState) => [
        ...prevState,
        {
          departureDate: addDays(departureDate, diffReturn),
          returnDate: returnDate,
        },
      ]);
    } else {
      setSearchQue((prevState) => [
        ...prevState,
        {
          departureDate: departureDate,
          returnDate: returnDate,
        },
      ]);
    }
  };

  const init = () => {
    setCheapestCombinations([]);
    setEmptyCheapestCombinations([]);
    const { isValid, nextFlights }: ISearchFormValidation =
      validateSearchForm(flights);
    if (isValid) {
      handleChangeDates();
    } else if (
      nextFlights.some(
        (flight) => flight.validation.departureDate.valid === false,
      )
    ) {
      setCustomErrorCodes(["noReturnDate"]);
      return;
    } else {
      return;
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <ReturnCalendar
      selectedItineraryId={selectedItineraryId}
      selectedItineraryDates={selectedItineraryDates}
      searchedDates={searchedDates}
      cheapestCombinations={[
        ...cheapestCombinations,
        ...emptyCheapestCombinations,
      ]}
      setSelectedItinerary={(id: string) =>
        setSelectedItinerary(cheapestItineraries[id])
      }
      loadMoreData={loadMoreData}
      showYouthStudentItineraries={showYouthStudentItineraries}
    />
  );
};
